import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'

import type {
  ChatDashboardConfig,
  ChatDashboardItem,
  CanvasActions,
  DashboardCanvasContent,
} from '@factorialco/f0-react'

import { savedDashboardConfigStore, savedDashboardMetaStore } from './configStore'
import { useSaveDashboardConfig } from './useSaveDashboardConfig'

// Mirror of f0's internal DashboardItemLayout (not part of the public types).
export type DashboardItemLayout = {
  id: string
  colSpan: number
  rowSpan: number
  itemHeight: number
  x: number
  y: number
}

export type PendingContext = {
  label: string
  context: string
}

type DashboardCanvasContextValue = {
  /** Whether there are unsaved layout changes */
  isDirty: boolean
  /** Incrementing counter — changes when discard is called to reset the grid */
  discardKey: number
  /** Pending item transforms keyed by item id (chart type changes, etc.) */
  itemTransforms: Map<string, Partial<ChatDashboardItem>>
  /** Called by the grid when layout changes (drag/resize/delete) */
  onLayoutChange: (layout: DashboardItemLayout[]) => void
  /** Persist current layout and update canvas */
  handleSave: () => Promise<void>
  /** Discard pending layout changes */
  handleDiscard: () => void
  /** Transform an item's config (e.g. change chart type). Marks dirty without refetch. */
  transformItem: (itemId: string, patch: Partial<ChatDashboardItem>) => void
  /** Persist arbitrary config to chat history (best-effort). */
  saveConfigToHistory: (config: Record<string, unknown>) => Promise<void>
  /** Export the dashboard as Excel */
  exportAsExcel?: () => Promise<void>
  /** Register the export function from the dashboard component */
  registerExport: (fn: (() => Promise<void>) | undefined) => void
  /** Optional save/create callbacks forwarded from the host (`useAiChat`). */
  canvasActions?: CanvasActions
  /** Replace the current canvas content with a new one (e.g. after save-as). */
  onCanvasContentChange: (content: DashboardCanvasContent) => void
}

const DashboardCanvasContext = createContext<DashboardCanvasContextValue | null>(null)

export function useDashboardCanvas(): DashboardCanvasContextValue {
  const ctx = useContext(DashboardCanvasContext)
  if (!ctx) {
    throw new Error('useDashboardCanvas must be used within DashboardProvider')
  }
  return ctx
}

export type DashboardProviderProps = {
  content: DashboardCanvasContent
  children: ReactNode
  /** Save/create callbacks. Optional — when absent, save buttons render as no-ops. */
  canvasActions?: CanvasActions
  /** Replace the current canvas content with a new one (e.g. after save). */
  onCanvasContentChange: (content: DashboardCanvasContent) => void
  /** Current pending context — when present, save updates its serialised config. */
  pendingContext?: PendingContext | null
  setPendingContext?: (ctx: PendingContext | null) => void
  /** Current CopilotKit thread id. Used to gate history persistence. */
  threadId?: string
}

export function DashboardProvider({
  content,
  children,
  canvasActions,
  onCanvasContentChange,
  pendingContext,
  setPendingContext,
  threadId,
}: DashboardProviderProps): ReactNode {
  const [pendingLayout, setPendingLayout] = useState<DashboardItemLayout[] | null>(null)
  const [itemTransforms, setItemTransforms] = useState<Map<string, Partial<ChatDashboardItem>>>(
    new Map()
  )
  const [discardKey, setDiscardKey] = useState(0)
  const [exportAsExcel, setExportAsExcel] = useState<(() => Promise<void>) | undefined>()
  const registerExport = useCallback((fn: (() => Promise<void>) | undefined) => {
    setExportAsExcel(() => fn)
  }, [])

  const saveConfigFn = useSaveDashboardConfig(content.apiConfig)

  const applyLayout = useCallback(
    (layout: DashboardItemLayout[]): ChatDashboardConfig | null => {
      const itemsById = new Map(content.config.items.map((item) => [item.id, item]))
      const newItems = layout
        .map((entry) => {
          const original = itemsById.get(entry.id)
          if (!original) return null
          return {
            ...original,
            colSpan: entry.colSpan,
            // Persist canonical pixel-accurate height from the resize.
            // `rowSpan` is stripped on persistence so subsequent renders
            // read `itemHeight` exclusively (avoids drift).
            rowSpan: undefined,
            itemHeight: entry.itemHeight,
            x: entry.x,
            y: entry.y,
          }
        })
        .filter((item) => item !== null)

      return { ...content.config, items: newItems }
    },
    [content]
  )

  const onLayoutChange = useCallback((layout: DashboardItemLayout[]) => {
    setPendingLayout(layout)
  }, [])

  const transformItem = useCallback((itemId: string, patch: Partial<ChatDashboardItem>) => {
    setItemTransforms((prev) => {
      const next = new Map(prev)
      next.set(itemId, patch)
      return next
    })
  }, [])

  /** Apply item transforms to a config, producing a new config with patched items. */
  const applyTransforms = useCallback(
    (config: ChatDashboardConfig): ChatDashboardConfig => {
      if (itemTransforms.size === 0) return config
      return {
        ...config,
        items: config.items.map((item) => {
          const patch = itemTransforms.get(item.id)
          // eslint-disable-next-line no-type-assertion/no-type-assertion -- structural patch over discriminated union
          return patch ? ({ ...item, ...patch } as typeof item) : item
        }),
      }
    },
    [itemTransforms]
  )

  const handleSave = async () => {
    const hasPendingChanges =
      pendingLayout || itemTransforms.size > 0 || content.savedDashboardUnsaved
    if (!hasPendingChanges) return

    let updatedConfig = pendingLayout ? applyLayout(pendingLayout) : { ...content.config }
    if (!updatedConfig) return

    updatedConfig = applyTransforms(updatedConfig)

    try {
      // Persist to chat history (best-effort).
      if (threadId) {
        try {
          const configWithMeta = {
            ...updatedConfig,
            ...(content.savedDashboardId
              ? {
                  savedDashboardId: content.savedDashboardId,
                  savedDashboardCategory: content.savedDashboardCategory,
                  savedDashboardDescription: content.savedDashboardDescription,
                  savedDashboardUnsaved: false,
                }
              : {}),
          }
          await saveConfigFn(
            threadId,
            content.toolCallId,
            // eslint-disable-next-line no-type-assertion/no-type-assertion -- metadata fields are additive at runtime
            configWithMeta as typeof updatedConfig
          )
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[Dashboard] saveConfigToHistory failed, continuing with external save', err)
        }
      }

      // External persistence via canvasActions.
      if (
        content.savedDashboardId &&
        content.savedDashboardCategory &&
        canvasActions?.dashboard?.save
      ) {
        await canvasActions.dashboard.save(
          content.savedDashboardId,
          content.savedDashboardCategory,
          updatedConfig
        )
      }

      if (content.toolCallId) {
        savedDashboardConfigStore.set(content.toolCallId, updatedConfig)
        savedDashboardMetaStore.set(content.toolCallId, {
          savedDashboardId: content.savedDashboardId,
          savedDashboardCategory: content.savedDashboardCategory,
          savedDashboardDescription: content.savedDashboardDescription,
          savedDashboardUnsaved: false,
        })
      }

      // Update pending context so the agent sees the latest config.
      if (pendingContext && setPendingContext) {
        setPendingContext({
          ...pendingContext,
          context: JSON.stringify({
            ...updatedConfig,
            savedDashboardId: content.savedDashboardId,
            savedDashboardCategory: content.savedDashboardCategory,
            savedDashboardDescription: content.savedDashboardDescription,
          }),
        })
      }

      onCanvasContentChange({
        ...content,
        config: updatedConfig,
        savedDashboardUnsaved: false,
      })

      setPendingLayout(null)
      setItemTransforms(new Map())
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Dashboard] save failed', err)
    }
  }

  const saveConfigToHistory = useCallback(
    async (config: Record<string, unknown>) => {
      if (!threadId) return
      try {
        await saveConfigFn(
          threadId,
          content.toolCallId,
          // eslint-disable-next-line no-type-assertion/no-type-assertion -- arbitrary persisted shape forwarded as ChatDashboardConfig
          config as unknown as ChatDashboardConfig
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Dashboard] saveConfigToHistory failed', err)
      }
    },
    [threadId, content.toolCallId, saveConfigFn]
  )

  const handleDiscard = () => {
    setPendingLayout(null)
    setItemTransforms(new Map())
    setDiscardKey((k) => k + 1)
  }

  return (
    <DashboardCanvasContext.Provider
      value={{
        isDirty: pendingLayout !== null || itemTransforms.size > 0,
        discardKey,
        itemTransforms,
        onLayoutChange,
        handleSave,
        handleDiscard,
        transformItem,
        saveConfigToHistory,
        exportAsExcel,
        registerExport,
        canvasActions,
        onCanvasContentChange,
      }}
    >
      {children}
    </DashboardCanvasContext.Provider>
  )
}
